<script setup>
// Історія коментарів з усього сайту: загальний чат, гілки аніме, серій і
// кліпів в одному списку, від нових до старих, і в кожного видно, куди його
// написали.
//
// Гілки тепер свої (backend/src/api/thread), тож список просто сторінкується
// з бекенда — без обходу comments.app у фоні. Старі коментарі потрапляють
// сюди, щойно їхню гілку перенесено (див. ensureImported).

const PAGE_SIZE = 30

const config = useRuntimeConfig()
const token = useStrapiToken()

const items = ref([])
const total = ref(0)
const loading = ref(true)
const loadingMore = ref(false)
const failed = ref(false)

const hasMore = computed(() => items.value.length < total.value)

const fetchPage = (offset) =>
  $fetch(`${config.public.apiBase}/api/threads/latest`, {
    query: { limit: PAGE_SIZE, offset },
    headers: token.value ? { Authorization: `Bearer ${token.value}` } : {}
  })

const load = async () => {
  try {
    const data = await fetchPage(0)
    items.value = data.comments
    total.value = data.total
    failed.value = false
  } catch (e) {
    failed.value = true
  }
  loading.value = false
}

const loadMore = async () => {
  if (loadingMore.value || !hasMore.value) return
  loadingMore.value = true
  try {
    const data = await fetchPage(items.value.length)
    const seen = new Set(items.value.map((item) => item.id))
    items.value = [...items.value, ...data.comments.filter((item) => !seen.has(item.id))]
    total.value = data.total
  } catch (e) {
    // Мовчки: кнопка лишається на місці, можна спробувати ще раз
  } finally {
    loadingMore.value = false
  }
}

onMounted(load)

// --- Куди був залишений коментар ---

const targetLink = (target) => {
  if (target?.kind === 'episode') {
    // ?ep= — той самий формат, який плеєр розуміє при відкритті сторінки
    return `/watch/${target.slug}?ep=${target.episodeIndex}`
  }
  if (target?.kind === 'anime') return `/watch/${target.slug}`
  if (target?.kind === 'clip') return `/clips/${target.clipId}`
  return '/'
}

const targetLabel = (target) => {
  if (target?.kind === 'episode') return target.animeTitle
  if (target?.kind === 'anime') return target.animeTitle
  if (target?.kind === 'clip') return `Кліп · ${target.clipTitle}`
  return 'Чат сайту'
}

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('uk-UA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

useHead({ title: 'Коментарі — Олсіор дивиться аніме' })
</script>

<template>
  <div class="mx-auto px-[10px] py-[20px] max-w-[1350px] min-h-screen">
    <div class="flex items-baseline gap-3 mb-[6px]">
      <h1 class="font-bold text-2xl">{{ $t('comments') }}</h1>
      <span
        v-if="total"
        class="text-sm text-base-content/50"
        >{{ total }}</span
      >
    </div>

    <p class="mb-[20px] text-sm text-base-content/50">
      Усе, що написали в чаті сайту, під аніме, серіями й кліпами — від нових
      до старих.
    </p>

    <div
      v-if="loading"
      class="flex flex-col gap-2"
    >
      <div
        v-for="n in 6"
        :key="n"
        class="bg-neutral/50 rounded-[12px] h-[86px] animate-pulse"
      />
    </div>

    <p
      v-else-if="failed"
      class="py-[40px] text-base-content/50 text-center"
    >
      Не вдалося завантажити коментарі
    </p>

    <p
      v-else-if="!items.length"
      class="py-[40px] text-base-content/50 text-center"
    >
      Коментарів ще немає
    </p>

    <div
      v-else
      class="flex flex-col gap-2"
    >
      <article
        v-for="msg in items"
        :key="msg.id"
        class="flex gap-3 bg-neutral/50 p-[12px] rounded-[12px]"
      >
        <CommentAvatar
          :author="msg.author"
          :avatar="msg.avatar || (msg.badge ? useStrapiMedia(msg.badge) : '')"
          :size="36"
        />

        <div class="min-w-0 grow">
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              class="font-semibold text-sm"
              :style="{ color: msg.color || nickColor(msg.author) }"
              >{{ msg.author }}</span
            >

            <VerifiedBadge v-if="msg.verified" />

            <span class="text-[11px] text-base-content/40">{{
              formatDate(msg.at)
            }}</span>
          </div>

          <!-- Куди саме написали: клік веде на ту саму сторінку -->
          <NuxtLink
            :to="targetLink(msg.target)"
            class="inline-flex items-center gap-1.5 bg-base-100/60 hover:bg-base-100 mt-1.5 px-2 py-[3px] rounded-full max-w-full text-[11px] transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              class="text-base-content/40 shrink-0"
            >
              <path
                fill="currentColor"
                d="M4 6H2v14a2 2 0 0 0 2 2h14v-2H4zm16-4H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2m-9 12V6l7 4z"
              />
            </svg>
            <span class="truncate">{{ targetLabel(msg.target) }}</span>
            <span
              v-if="msg.target?.kind === 'episode'"
              class="text-base-content/50 truncate shrink-0"
              >· {{ msg.target.episodeTitle }}</span
            >
          </NuxtLink>

          <!-- Кому відповіли -->
          <div
            v-if="msg.replyTo"
            class="mt-1.5 pl-2 border-primary/40 border-l text-[12px] text-base-content/50 line-clamp-2"
          >
            <span class="font-medium">{{ msg.replyTo.author }}:&nbsp;</span>
            <EmoteText :text="msg.replyTo.text" />
          </div>

          <EmoteText
            class="block mt-1 text-sm whitespace-pre-line"
            :text="msg.text"
          />
          <ChatMediaGrid
            v-if="msg.media?.length"
            :media="msg.media"
            :spoiler="msg.spoiler"
          />
        </div>
      </article>

      <button
        v-if="hasMore"
        class="mt-[10px] btn btn-ghost"
        :disabled="loadingMore"
        @click="loadMore"
      >
        <span
          v-if="loadingMore"
          class="loading loading-spinner loading-sm"
        />
        Показати ще
      </button>


    </div>
  </div>
</template>
